import { REPORT_TYPE_KEYS } from "@utils/utils";
import clsx from "clsx";
import { useMemo } from "react";

import { BadgeProps, Column, Small, Table } from "@fronterahealth/frontera-ui-components";
import { Switch } from "@fronterahealth/frontera-ui-components/ui/switch";

import {
  ApiReportReportTypeChoices,
  ReportReviewCountsType,
  ReportReviewView,
  ReportTemplateType,
  ReportTypeEnums,
  useDeleteReportMutation,
} from "@api/graphql/types-and-hooks";
import { REVIEW_VIEWS, REVIEW_VIEW_LABELS } from "@components/ReportReview/reviewViews";
import {
  buildViewDeleteReportActions,
  clientNameColumn,
  createdByColumn,
  dateCreatedColumn,
  lastUpdatedColumn,
  reportTemplateColumn,
  statusColumn,
} from "@components/ReportsTableShared/reportsTableShared";
import { StatusLegendTooltip } from "@components/StatusLegendTooltip/StatusLegendTooltip";
import { TableFilterDropdown } from "@components/TableFilterDropdown/TableFilterDropdown";
import { InitialAssessmentPageRoute } from "@pages/AssessmentReportV2Details/types";
import { useAdminData } from "@providers/AdminDataProvider";
import { ClinicalReportPageRoute } from "@routes/ClinicalReports/ClinicalReportsHelper";

export const STATUS_VALUES = [
  "Draft",
  "Complete",
  "Submitted",
  "Needs Revision",
  "Approved",
  "Import Failed",
  "Refreshing data…",
] as const;
export type StatusType = (typeof STATUS_VALUES)[number];

export const BadgeSettingFromStatus: { [key in StatusType]: BadgeProps["appearance"] } = {
  Draft: "neutral",
  Complete: "success",
  Submitted: "neutral",
  "Needs Revision": "warn",
  Approved: "success",
  "Import Failed": "error",
  "Refreshing data…": "ai",
};

// Statuses worth exposing as filter options. Excludes the transient/in-flight
// values ("Refreshing data…", "Import Failed") which aren't useful filter targets.
const FILTERABLE_STATUSES: StatusType[] = ["Draft", "Complete", "Submitted", "Needs Revision", "Approved"];

const FILTERABLE_TYPES: ReportTypeEnums[] = [
  ReportTypeEnums.InitialAssessment,
  ReportTypeEnums.Reassessment,
  ReportTypeEnums.DiagnosisEvaluation,
];

export interface ReviewFilterProps {
  view: ReportReviewView | undefined;
  onChange: (next: ReportReviewView | undefined) => void;
  counts?: Pick<ReportReviewCountsType, "waitingOnMe" | "sentBackToMe" | "inReview" | "reviewedByMe">;
}

interface ClinicalReportsTableProps {
  data: (ClinicalReport | null)[];
  onRowClick: (report: ClinicalReport) => void;
  loading?: boolean;
  refetch: () => Promise<unknown>;
  onPageChange: (currentPage: number, itemsPerPage: number) => void;
  pageItems: number;
  totalItemCount: number;
  searchTerm: string;
  setSearchTerm: (searchTerm: string) => void;
  statusFilter: StatusType[];
  onStatusFilterChange: (next: StatusType[]) => void;
  typeFilter: ReportTypeEnums[];
  onTypeFilterChange: (next: ReportTypeEnums[]) => void;
  myReportsOnly: boolean;
  onMyReportsOnlyChange: (next: boolean) => void;
  /** Omit to hide the Review filter and column (peer review off). */
  review?: ReviewFilterProps;
}

export interface ClinicalReport {
  id: string;
  clientName: string;
  reportType: ApiReportReportTypeChoices;
  reportTemplate: ReportTemplateType | null;
  createdBy: string;
  dateCreated: Date;
  lastUpdated: Date;
  status: StatusType;
  reviewState?: ReportReviewView | null;
  redirectRoute: ClinicalReportPageRoute | InitialAssessmentPageRoute;
}

export const ClinicalReportsTable: React.FC<ClinicalReportsTableProps> = ({
  data,
  onRowClick,
  loading,
  refetch,
  onPageChange,
  pageItems,
  totalItemCount,
  searchTerm,
  setSearchTerm,
  statusFilter,
  onStatusFilterChange,
  typeFilter,
  onTypeFilterChange,
  myReportsOnly,
  onMyReportsOnlyChange,
  review,
}) => {
  const deleteReportMutation = useDeleteReportMutation({});

  const { roles } = useAdminData();

  const actions = useMemo(
    () =>
      buildViewDeleteReportActions<ClinicalReport>({
        isInternalUser: roles.includes("frontera-internal"),
        onRowClick,
        deleteReport: (report) => deleteReportMutation.mutateAsync({ report }),
        refetch,
      }),
    [roles],
  );

  const columns = [
    clientNameColumn<ClinicalReport>(review ? "w-[16%]" : "w-[18%]"),
    {
      dataIndex: "reportType",
      key: "reportType",
      title: "Type",
      className: review ? "w-[9%]" : "w-[10%]",
      render: (value: ApiReportReportTypeChoices) =>
        value ? REPORT_TYPE_KEYS[value as unknown as ReportTypeEnums] : "",
    },
    reportTemplateColumn<ClinicalReport>(review ? "w-[16%]" : "w-[18%]"),
    createdByColumn<ClinicalReport>(review ? "w-[12%]" : "w-[14%]"),
    dateCreatedColumn<ClinicalReport>(review ? "w-[11%]" : "w-[12%]"),
    lastUpdatedColumn<ClinicalReport>(review ? "w-[11%]" : "w-[12%]"),
    statusColumn<ClinicalReport>(review ? "w-[12%]" : "w-[14%]"),
    ...(review
      ? [
          {
            dataIndex: "reviewState",
            key: "reviewState",
            title: "Review",
            className: "w-[11%]",
            render: (value: ReportReviewView | null | undefined) => (value ? REVIEW_VIEW_LABELS[value] : ""),
          },
        ]
      : []),
  ];

  const statusFilterItems = useMemo(() => FILTERABLE_STATUSES.map((status) => ({ id: status, label: status })), []);
  const typeFilterItems = useMemo(
    () => FILTERABLE_TYPES.map((type) => ({ id: type, label: REPORT_TYPE_KEYS[type] })),
    [],
  );

  const reviewCounts = review?.counts && {
    [ReportReviewView.WaitingOnMe]: review.counts.waitingOnMe,
    [ReportReviewView.SentBackToMe]: review.counts.sentBackToMe,
    [ReportReviewView.InReview]: review.counts.inReview,
    [ReportReviewView.ReviewedByMe]: review.counts.reviewedByMe,
  };
  const reviewFilterItems = REVIEW_VIEWS.map((view) => ({
    id: view,
    label: REVIEW_VIEW_LABELS[view],
    count: reviewCounts?.[view],
  }));

  // The server filter takes one view, so the multi-select dropdown acts as a single select.
  const handleReviewChange = (next: ReportReviewView[]) => review?.onChange(next.find((view) => view !== review.view));

  const filterDropdowns = (
    <div className="flex items-center gap-2">
      <div className="mr-2 flex items-center gap-2">
        <Small colorType="primary">My Reports</Small>
        <Switch size="sm" checked={myReportsOnly} onCheckedChange={onMyReportsOnlyChange} />
      </div>
      <TableFilterDropdown<ReportTypeEnums>
        label="Type"
        items={typeFilterItems}
        value={typeFilter}
        onChange={onTypeFilterChange}
      />
      {review && (
        <TableFilterDropdown<ReportReviewView>
          label="Review"
          items={reviewFilterItems}
          value={review.view ? [review.view] : []}
          onChange={handleReviewChange}
        />
      )}
      <TableFilterDropdown<StatusType>
        label="Status"
        items={statusFilterItems}
        value={statusFilter}
        onChange={onStatusFilterChange}
        headerSlot={<StatusLegendTooltip />}
      />
    </div>
  );

  // Filtering happens client-side over the current page, so paginating across
  // server pages would skip filtered-out rows on intervening pages and land on
  // empty pages. Hide pagination while either filter narrows the visible set.
  const isFiltering = statusFilter.length > 0 || typeFilter.length > 0;

  return (
    <div
      className={clsx(
        "mt-4 w-full [&_.flow-root>div>div>div]:min-h-[480px] [&_table]:!table-fixed [&_td>.text-center]:min-h-[300px]",
        // mt-auto pins pagination to the bottom of the 480px frame when rows
        // are sparse. With pagination hidden, that same rule pushes the table
        // itself to the bottom and leaves a giant gap above — only apply it
        // when pagination is visible.
        !isFiltering && "[&_.flow-root>div>div>div>:last-child]:mt-auto",
      )}
    >
      <Table
        dataTestId={"clinical-reports-table"}
        onRowClick={onRowClick}
        onPageChange={onPageChange}
        loading={loading}
        defaultItemsPerPage={pageItems}
        totalItemCount={totalItemCount}
        showSearch
        inputClassName="w-96 py-2.5"
        showPagination={!isFiltering}
        itemsPerPageOptions={[5, 10, 20, 50]}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        actions={actions}
        data={data as ClinicalReport[]}
        columns={columns as Column<ClinicalReport>[]}
        headerContent={filterDropdowns}
      />
    </div>
  );
};
